import { attach, combine, createDomain, sample } from 'effector'
import { FormValues, promotionModel } from '~/entities/Promotion'
import { AxiosErrorType } from '~/shared/api'
import { Promocode } from '~/shared/api/promocodes'
import { replaceFx } from '~/shared/lib/history'
import { logger } from '~/shared/lib/logger'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { isString } from '~/shared/lib/utils'
import { formButtonsModel } from '~/shared/ui/Form/FormButtons'

export const domain = createDomain('features.promotion.editPromotion')

export const $promotion = combine(
  promotionModel.$promotionId,
  promotionModel.$promotionCache,
  (id, cache) => {
    if (!id) return null
    return cache.map[id] ?? null
  },
)

export const formSubmitted = domain.createEvent<FormValues>()
export const promotionUpdateFx = attach({
  effect: promotionModel.savePromotionFx,
  source: promotionModel.$promotionId,
  mapParams: (values: FormValues, id) => {
    const {
      regionsIds,
      carBrandsIds,
      carModelsIds,
      carTypesTransmission,
      carTypesFuel,
      carCategories,
      carRegionsIds,
      ...restAttrs
    } = values

    const promotion = new Promocode(
      {
        ...restAttrs,
        carTypesTransmission: (carTypesTransmission || []).map((item) =>
          isString(item) ? item : item.id,
        ),
        carTypesFuel: (carTypesFuel || []).map((item) =>
          isString(item) ? item : item.id,
        ),
        carCategories: (carCategories || []).map((item) =>
          isString(item) ? item : item.label,
        ),
      },
      id as string,
    )

    promotion.setRegions(regionsIds || [])
    promotion.setCarBrands(carBrandsIds || [])
    promotion.setCarModels(carModelsIds || [])
    promotion.setCarRegions(carRegionsIds || [])

    return promotion
  },
})

sample({
  clock: formSubmitted,
  target: promotionUpdateFx,
})

sample({
  clock: promotionUpdateFx.doneData,
  fn() {
    return {
      message: 'Сохранено успешно',
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued, formButtonsModel.editingEnded],
})

sample({
  clock: promotionUpdateFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: 'Ошибка при сохранении данных',
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

export const $canEditPromotion = $promotion.map(
  (promotion) => promotion?.isPromotion() ?? false,
)

export const deletePromotion = domain.createEvent()
export const deletePromotionFx = domain.createEffect<
  UniqueId,
  void,
  AxiosErrorType
>({
  async handler(promotionId) {
    const promotion = new Promocode({}, promotionId)
    await promotion.delete()
  },
})

sample({
  clock: deletePromotion,
  source: promotionModel.$promotionId,
  filter: isString,
  target: deletePromotionFx,
})

sample({
  clock: deletePromotionFx.doneData,
  fn() {
    return {
      message: 'Удалено успешно',
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued],
})

sample({
  clock: deletePromotionFx.doneData,
  fn: () => '/promocodes',
  target: replaceFx,
})

sample({
  clock: deletePromotionFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: 'Ошибка при удалении акции',
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})
